/**
 * @license
 * Copyright 2024 Google LLC
 * SPDX-License-Identifier: Apache-2.0
 */
export class NodeCache {
    #factory;
    #map = new Map();
    #typeMap = new Map();
    constructor(factory) {
        this.#factory = factory;
    }
    rebuild(graph) {
        graph.nodes.forEach((node) => this.#addNodeInternal(node, ""));
        Object.entries(graph.graphs || {}).forEach(([graphId, graph]) => {
            graph.nodes.forEach((node) => this.#addNodeInternal(node, graphId));
        });
    }
    addSubgraphNodes(subgraph, graphId) {
        subgraph.nodes.forEach((node) => this.#addNodeInternal(node, graphId));
    }
    removeSubgraphNodes(graphId) {
        const subgraph = this.#map.get(graphId);
        subgraph?.forEach((node) => {
            node.setDeleted();
        });
        this.#map.delete(graphId);
    }
    #addNodeInternal(node, graphId) {
        const graphTypes = getOrCreate(this.#typeMap, graphId, () => new Map());
        const inspectableNode = this.#factory(node, graphId);
        const type = node.type;
        let list = graphTypes.get(type);
        if (!list) {
            list = [];
            graphTypes.set(type, list);
        }
        list.push(inspectableNode);
        const graphNodes = getOrCreate(this.#map, graphId, () => new Map());
        graphNodes.set(node.id, inspectableNode);
        return inspectableNode;
        function getOrCreate(map, key, factory) {
            let v = map.get(key);
            if (v)
                return v;
            v = factory();
            map.set(key, v);
            return v;
        }
    }
    byType(type, graphId) {
        return this.#typeMap.get(graphId)?.get(type) || [];
    }
    get(id, graphId) {
        return this.#map.get(graphId)?.get(id);
    }
    add(node, graphId) {
        if (!this.#map) {
            return;
        }
        this.#addNodeInternal(node, graphId);
    }
    remove(id, graphId) {
        if (!this.#map) {
            return;
        }
        const nodeMap = this.#map.get(graphId);
        if (!nodeMap) {
            console.error(`Can't remove node "${id}": graph "${graphId}" was not found`);
            return;
        }
        const node = nodeMap.get(id);
        console.assert(node, "Node does not exist in cache.");
        const type = node.descriptor.type;
        const list = this.#typeMap?.get(graphId)?.get(type);
        if (list) {
            const index = list.indexOf(node);
            list.splice(index, 1);
        }
        nodeMap.delete(id);
        node.setDeleted();
    }
    nodes(graphId) {
        return Array.from(this.#map.get(graphId)?.values() || []);
    }
}
//# sourceMappingURL=node-cache.js.map